import type { StudySelectDto } from '@@types/api.types';
import { ClassificationBadge } from '@components/shared/classification-badge.component';
import { StudyStatusBadge } from '@components/shared/study-status-badge.component';
import { Layers3Icon, UserRoundIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { MeiaStudyTopHeader } from './meia-study-top-header.component';

type MeiaStudyDetailHeaderProps = {
  acrSlot?: ReactNode;
  canEdit: boolean;
  children: ReactNode;
  missionsCount: number;
  onEdit: () => void;
  phaseLabel?: string;
  study: StudySelectDto;
  threadsLabel?: string;
};

const MetadataSeparator: FC = () => <span aria-hidden="true">·</span>;

export const MeiaStudyDetailHeader: FC<MeiaStudyDetailHeaderProps> = (props) => {
  const { acrSlot, canEdit, children, missionsCount, onEdit, phaseLabel, study, threadsLabel } = props;

  return (
    <div
      className="sticky top-(--impersonation-banner-height,0px) z-5 -mx-4 -mt-4 flex w-[calc(100%+2rem)] flex-col overflow-x-hidden border-b border-gray-200 bg-white pt-4 shadow-sm before:absolute before:inset-0 before:left-1/2 before:-z-10 before:w-svw before:-translate-x-1/2 before:bg-white"
      data-meia-study-header="true"
    >
      <div className="@container/study-detail-header mx-auto w-full max-w-[1600px] px-8">
        <div className="w-full pb-4 pt-2">
          <MeiaStudyTopHeader
            acrSlot={acrSlot}
            canEdit={canEdit}
            onEdit={onEdit}
            phaseLabel={phaseLabel}
            study={study}
          />

          <div className="flex w-full justify-between gap-4 py-1">
            <div className="flex min-w-0 flex-col gap-2">
              <h1 className="text-xl font-medium text-gray-800">{study.name}</h1>

              <div className="flex flex-wrap items-center gap-2">
                <ClassificationBadge classification={study.classificationLevel} />
                <StudyStatusBadge status={study.status} />
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <Layers3Icon className="size-3" />
                  {missionsCount} {missionsCount === 1 ? 'mission' : 'missions'}
                </span>
                {threadsLabel && (
                  <>
                    <MetadataSeparator />
                    <span>{threadsLabel}</span>
                  </>
                )}
                <MetadataSeparator />
                <span className="inline-flex items-center gap-1">
                  <UserRoundIcon className="size-3" />
                  Author: <span className="text-gray-700">{study.ownerName || '—'}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {children}
      </div>
    </div>
  );
};
